import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PlayerProfile } from '../../../core/models/player-profile';
import { playerProfileApi } from '../../../core/http/player-profile.api';
import { profileChart } from './profile-chart';
import { GameService } from '../../../core/state/game.service';
import { Player, getUnavailabilityTypeLabel, getUnavailabilityReasonLabel } from '../../../core/models/types';

@Component({
  selector: 'app-player-profile',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './player-profile.component.html',
  styleUrl: './player-profile.component.scss',
})
export class PlayerProfileComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly playerDefinitionId = input.required<string>();
  readonly playerName = input<string>('');
  readonly dismissible = input(true);
  readonly getUnavailabilityTypeLabel = getUnavailabilityTypeLabel;
  readonly getUnavailabilityReasonLabel = getUnavailabilityReasonLabel;
  readonly closed = output<void>();
  readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');
  readonly profile = signal<PlayerProfile | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly retryCount = signal(0);
  readonly chart = computed(() => profileChart(this.profile()?.history ?? []));
  readonly game = inject(GameService);
  readonly isListingFormOpen = signal<boolean>(false);
  readonly askingPriceInput = signal<number | null>(null);

  readonly ownedPlayer = computed<Player | undefined>(() => {
    const defId = this.playerDefinitionId();
    const prof = this.profile();
    const profDefId = prof?.playerDefinitionId;
    const squad = this.game.userSquad ? (this.game.userSquad() || []) : [];

    return squad.find(p => {
      if (p.playerDefinitionId && (p.playerDefinitionId === defId || p.playerDefinitionId === profDefId)) {
        return true;
      }
      if (p.id === defId || (profDefId && p.id === profDefId)) {
        return true;
      }
      if (p.playerInstanceId && (p.playerInstanceId === defId || (profDefId && p.playerInstanceId === profDefId))) {
        return true;
      }
      if (prof?.displayName && p.name && p.name.trim().toLowerCase() === prof.displayName.trim().toLowerCase()) {
        if (!prof.realClub || !p.realClub || prof.realClub.id === p.realClub.id || prof.realClub.shortName === p.realClub.shortName) {
          return true;
        }
      }
      return false;
    });
  });

  readonly isPending = computed<boolean>(() => {
    const player = this.ownedPlayer();
    if (!player || !this.game.transferPending) return false;
    return this.game.transferPending().has(player.id);
  });

  private previousOverflow = '';
  private dialogElement?: HTMLDialogElement;

  ngOnInit(): void {
    const clubId = this.game.activeUserClubId?.() || this.game.userClub?.()?.id;
    if (clubId && clubId !== 'default' && this.game.userSquad && this.game.userSquad().length === 0) {
      void this.game.fetchSquadForClub?.(clubId, false);
    }
  }

  constructor() {
    effect((onCleanup) => {
      const id = this.playerDefinitionId();
      this.retryCount();
      let active = true;
      onCleanup(() => { active = false; });
      this.loading.set(true);
      this.error.set(null);
      this.profile.set(null);
      playerProfileApi.get(id).then(profile => {
        if (active) this.profile.set(profile);
      }).catch(error => {
        if (active) this.error.set(error?.status === 404
          ? 'No se ha encontrado la ficha de este jugador.'
          : 'No se ha podido cargar la ficha del jugador. Inténtalo de nuevo.');
      }).finally(() => { if (active) this.loading.set(false); });
    });
  }

  ngAfterViewInit(): void {
    this.dialogElement = this.dialog().nativeElement;
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.dialogElement.showModal();
  }

  ngOnDestroy(): void {
    this.dialogElement?.close();
    document.body.style.overflow = this.previousOverflow;
  }

  close(): void {
    this.isListingFormOpen.set(false);
    this.dialog().nativeElement.close();
    this.closed.emit();
  }

  openListingForm(player: Player): void {
    const defaultPrice = player.askingPrice ??
      (player.contract?.releaseClause
        ? player.contract.releaseClause
        : (this.profile()?.marketValue ? Number((this.profile()!.marketValue! / 1_000_000).toFixed(1)) : 5.0));
    this.askingPriceInput.set(defaultPrice);
    this.isListingFormOpen.set(true);
  }

  onAskingPriceChange(event: Event): void {
    const val = parseFloat((event.target as HTMLInputElement).value);
    this.askingPriceInput.set(isNaN(val) ? null : val);
  }

  async confirmListing(): Promise<void> {
    const player = this.ownedPlayer();
    if (!player || this.isPending()) return;
    const price = this.askingPriceInput();
    if (!price || price <= 0) return;
    const success = await this.game.listPlayerForSale?.(player.id, price);
    if (success !== false) {
      this.isListingFormOpen.set(false);
    }
  }

  async withdrawFromSale(player: Player): Promise<void> {
    if (this.isPending()) return;
    await this.game.removePlayerFromSale?.(player.id);
  }

  retry(): void {
    this.retryCount.update(count => count + 1);
  }

  backdrop(event: MouseEvent): void {
    if (!this.dismissible()) return;
    if (event.target !== this.dialog().nativeElement) return;
    const bounds = this.dialog().nativeElement.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
  }

  clubShield(player: PlayerProfile) {
    const code = player.realClub?.shortName?.toUpperCase();
    return code ? `/crest/${code}.png` : null;
  }

  positionText(player: PlayerProfile) {
    const pos = this.position(player.position);
    const sec = player.secondaryPositions?.map(p => this.position(p)).join(', ');
    return sec ? `${pos} (${sec})` : pos;
  }

  positionBadgeClass(value: string): string {
    switch (value) {
      case 'FORWARD': return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-300 dark:border-rose-500/40';
      case 'MIDFIELDER': return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40';
      case 'DEFENDER': return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/40';
      case 'GOALKEEPER': return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40';
      default: return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
    }
  }

  onCrestError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (!img.dataset['triedFallback']) {
      img.dataset['triedFallback'] = 'true';
      img.src = img.src.replace('/crest/', '/assets/crest/');
    } else img.style.display = 'none';
  }

  marketMillions(value: number | null): string {
    return value === null ? 'Sin datos' : `${(value / 1_000_000).toFixed(1)} M€`;
  }

  position(value: string): string {
    return ({ GOALKEEPER: 'POR', DEFENDER: 'DEF', MIDFIELDER: 'MED', FORWARD: 'DEL' } as Record<string, string>)[value] ?? value;
  }

  fullPosition(value: string): string {
    return ({ GOALKEEPER: 'Portero', DEFENDER: 'Defensa', MIDFIELDER: 'Centrocampista', FORWARD: 'Delantero' } as Record<string, string>)[value] ?? value;
  }

  number(value: number | null): string {
    return value === null ? 'Sin datos' : new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 }).format(value);
  }

  money(value: number | null): string {
    return value === null ? 'Sin datos' : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  }
}
